<script setup>
import flatpickr from 'flatpickr';
import { Dutch } from 'flatpickr/dist/l10n/nl.js';
import 'flatpickr/dist/flatpickr.min.css';
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { isClosedDate } from '../../shared/closed-dates.js';

const props = defineProps({
  modelValue: { type: String, default: '' },
  min: { type: String, default: '' },
  disable: { type: Array, default: () => [] },
  /** getDay()-waarden die niet selecteerbaar zijn (0=zo, 6=za) */
  disableWeekdays: { type: Array, default: () => [] },
  showMonths: { type: Number, default: 2 },
  id: { type: String, required: true },
  label: { type: String, default: '' }
});

const emit = defineEmits(['update:modelValue']);

const rootRef = ref(null);
const inputRef = ref(null);
const wrapRef = ref(null);
const fpRef = ref(null);

let lastPickerValue = props.modelValue || '';
let stopCalendarPropagation = null;

function formatYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isDateBlocked(date) {
  if (!date || typeof date.getTime !== 'function' || Number.isNaN(date.getTime())) {
    return true;
  }
  const ymd = formatYmd(date);
  if (isClosedDate(ymd)) return true;
  if (props.disable?.length && props.disable.includes(ymd)) return true;
  if (props.disableWeekdays?.length && props.disableWeekdays.includes(date.getDay())) {
    return true;
  }
  return false;
}

function buildDisableRules() {
  return [(date) => isDateBlocked(date)];
}

function applyDisableRules(fp) {
  if (!fp) return;
  fp.set('disable', buildDisableRules());
  fp.redraw();
}

function revertPickerSelection(fp) {
  if (!fp) return;
  if (lastPickerValue) {
    fp.setDate(lastPickerValue, false);
  } else {
    fp.clear(false);
  }
}

function syncFromPicker(dateStr) {
  const fp = fpRef.value;
  if (!dateStr) {
    lastPickerValue = '';
    emit('update:modelValue', '');
    return;
  }

  const parsed = fp?.parseDate(dateStr, 'Y-m-d');
  if (!parsed || isDateBlocked(parsed)) {
    revertPickerSelection(fp);
    return;
  }

  lastPickerValue = dateStr;
  emit('update:modelValue', lastPickerValue);
}

function applyMinDate(fp, value) {
  fp.set('minDate', value || 'today');
  applyDisableRules(fp);
}

function openPicker() {
  const fp = fpRef.value;
  if (!fp) return;
  if (fp.isOpen) {
    fp.close();
  } else {
    fp.open();
  }
}

function onWrapClick(event) {
  if (event.target.closest('.vsb-date-field__icon-btn')) return;
  openPicker();
}

function setupCalendarInteraction(instance) {
  const cal = instance.calendarContainer;
  if (!cal) return;

  const ignored = instance.config.ignoredFocusElements || [];
  [cal, instance.altInput, wrapRef.value, rootRef.value].forEach((el) => {
    if (el && !ignored.includes(el)) {
      ignored.push(el);
    }
  });
  instance.config.ignoredFocusElements = ignored;

  stopCalendarPropagation = (event) => {
    event.stopPropagation();
  };
  cal.addEventListener('mousedown', stopCalendarPropagation);
  cal.addEventListener('click', stopCalendarPropagation);
}

function resolveShowMonths() {
  if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
    return 1;
  }
  return props.showMonths;
}

function applyShowMonths(fp) {
  if (!fp) return;
  const next = resolveShowMonths();
  if (fp.config.showMonths !== next) {
    fp.set('showMonths', next);
    fp.redraw();
  }
}

let showMonthsMq = null;
let showMonthsMqListener = null;

onMounted(() => {
  if (!inputRef.value || !wrapRef.value) return;

  const fp = flatpickr(inputRef.value, {
    locale: Dutch,
    dateFormat: 'Y-m-d',
    altInput: true,
    altFormat: 'j F Y',
    minDate: props.min || 'today',
    disable: buildDisableRules(),
    defaultDate: props.modelValue || undefined,
    allowInput: false,
    clickOpens: false,
    disableMobile: true,
    monthSelectorType: 'dropdown',
    showMonths: resolveShowMonths(),
    onChange(_selectedDates, dateStr) {
      syncFromPicker(dateStr);
    },
    onOpen(_dates, _str, instance) {
      applyShowMonths(instance);
    },
    onReady(_dates, _str, instance) {
      setupCalendarInteraction(instance);
      applyShowMonths(instance);
      const alt = instance.altInput;
      if (alt) {
        alt.classList.add('vsb-home-search__date', 'vsb-date-field__alt');
        alt.setAttribute('id', props.id);
        alt.setAttribute('aria-labelledby', props.id + '-label');
        alt.placeholder = 'Kies datum';
        alt.addEventListener('click', (event) => {
          event.stopPropagation();
          openPicker();
        });
      }
      if (props.modelValue) {
        const parsed = instance.parseDate(props.modelValue, 'Y-m-d');
        if (parsed && isDateBlocked(parsed)) {
          instance.clear(false);
          lastPickerValue = '';
        }
      }
    }
  });

  fpRef.value = fp;
  lastPickerValue = props.modelValue || '';

  if (typeof window !== 'undefined') {
    showMonthsMq = window.matchMedia('(max-width: 768px)');
    showMonthsMqListener = () => applyShowMonths(fpRef.value);
    showMonthsMq.addEventListener('change', showMonthsMqListener);
  }
});

watch(
  () => props.min,
  (v) => {
    if (fpRef.value) {
      applyMinDate(fpRef.value, v);
      if (props.modelValue && v && props.modelValue < v) {
        syncFromPicker(v);
      }
    }
  }
);

watch(
  () => [props.disable, props.disableWeekdays],
  () => {
    applyDisableRules(fpRef.value);
    if (props.modelValue) {
      const fp = fpRef.value;
      const parsed = fp?.parseDate(props.modelValue, 'Y-m-d');
      if (parsed && isDateBlocked(parsed)) {
        lastPickerValue = '';
        fp?.clear(false);
        emit('update:modelValue', '');
      }
    }
  },
  { deep: true }
);

watch(
  () => props.modelValue,
  (v) => {
    if (!fpRef.value) return;
    const next = v || '';
    if (next === lastPickerValue) return;

    if (next) {
      const parsed = fpRef.value.parseDate(next, 'Y-m-d');
      if (!parsed || isDateBlocked(parsed)) {
        revertPickerSelection(fpRef.value);
        return;
      }
    }

    lastPickerValue = next;
    if (next) {
      fpRef.value.setDate(next, false);
    } else {
      fpRef.value.clear();
    }
  }
);

onUnmounted(() => {
  if (showMonthsMq && showMonthsMqListener) {
    showMonthsMq.removeEventListener('change', showMonthsMqListener);
  }
  showMonthsMq = null;
  showMonthsMqListener = null;

  const cal = fpRef.value?.calendarContainer;
  if (cal && stopCalendarPropagation) {
    cal.removeEventListener('mousedown', stopCalendarPropagation);
    cal.removeEventListener('click', stopCalendarPropagation);
  }
  fpRef.value?.destroy();
  fpRef.value = null;
});
</script>

<template>
  <div ref="rootRef" class="vsb-home-search__field vsb-date-field">
    <label
      v-if="label"
      :id="id + '-label'"
      class="vsb-home-search__label"
      :for="id"
    >{{ label }}</label>
    <div ref="wrapRef" class="vsb-date-field__input-wrap" @click="onWrapClick">
      <input
        :id="id + '-native'"
        ref="inputRef"
        type="text"
        class="vsb-date-field__native"
        data-input
        tabindex="-1"
        aria-hidden="true"
      />
      <button
        type="button"
        class="vsb-date-field__icon-btn"
        tabindex="-1"
        aria-label="Kalender openen"
        @click.stop="openPicker"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" stroke-width="1.75" />
          <path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" />
        </svg>
      </button>
    </div>
  </div>
</template>

<style scoped>
.vsb-date-field {
  --vsb-hs-border: #e2e8f0;
  --vsb-hs-accent: #004494;
  --vsb-hs-text: #1e293b;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  position: relative;
  z-index: 2;
  overflow: visible;
}

.vsb-home-search__label {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--vsb-hs-text);
  letter-spacing: 0.02em;
}

.vsb-date-field__input-wrap {
  position: relative;
  width: 100%;
  cursor: pointer;
  pointer-events: auto;
}

.vsb-date-field__native {
  position: absolute;
  width: 1px;
  height: 1px;
  opacity: 0;
  pointer-events: none;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}

.vsb-date-field__icon-btn {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #004494;
  cursor: pointer;
  z-index: 4;
  pointer-events: auto;
}

.vsb-date-field__icon-btn:hover {
  background: #eff6ff;
}

.vsb-date-field :deep(.vsb-date-field__alt),
.vsb-date-field :deep(input.vsb-home-search__date) {
  font: inherit;
  font-size: 0.95rem;
  padding: 10px 44px 10px 12px;
  border: 1px solid var(--vsb-hs-border);
  border-radius: 8px;
  color: var(--vsb-hs-text);
  background: #fff;
  width: 100% !important;
  min-height: 44px;
  box-sizing: border-box;
  cursor: pointer;
  pointer-events: auto;
  position: relative;
  z-index: 3;
  display: block;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.vsb-date-field :deep(.vsb-date-field__alt:hover),
.vsb-date-field :deep(input.vsb-home-search__date:hover) {
  border-color: #94a3b8;
}

.vsb-date-field :deep(.vsb-date-field__alt:focus),
.vsb-date-field :deep(input.vsb-home-search__date:focus) {
  outline: 2px solid var(--vsb-hs-accent);
  outline-offset: 2px;
  border-color: var(--vsb-hs-accent);
}

@media (max-width: 768px) {
  .vsb-home-search__label {
    font-size: 0.75rem;
  }

  .vsb-date-field :deep(.vsb-date-field__alt),
  .vsb-date-field :deep(input.vsb-home-search__date) {
    font-size: 0.82rem;
    padding: 8px 38px 8px 10px;
    min-height: 40px;
  }

  .vsb-date-field__icon-btn {
    width: 32px;
    height: 32px;
  }

  .vsb-date-field__icon-btn svg {
    width: 16px;
    height: 16px;
  }
}
</style>

<style>
/* flatpickr staat op document.body — buiten scoped widget-stacking */
.flatpickr-calendar.open {
  z-index: 9999 !important;
  pointer-events: auto !important;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 12px 32px rgba(0, 68, 148, 0.16);
  font-family: inherit;
}

.flatpickr-current-month {
  font-size: 1rem;
  font-weight: 700;
  color: #004494;
}

.flatpickr-current-month .flatpickr-monthDropdown-months {
  font-weight: 700;
  color: #004494;
}

.flatpickr-weekday {
  color: #64748b;
  font-weight: 600;
  font-size: 0.75rem;
}

.flatpickr-day {
  border-radius: 8px;
  color: #1e293b;
  font-weight: 500;
}

.flatpickr-day.today {
  border-color: #0066cc;
}

.flatpickr-day.selected,
.flatpickr-day.startRange,
.flatpickr-day.endRange {
  background: #004494;
  border-color: #004494;
  color: #fff;
  font-weight: 600;
}

.flatpickr-day:not(.flatpickr-disabled):not(.prevMonthDay):not(.nextMonthDay):hover {
  background: #eff6ff;
  border-color: #eff6ff;
}

.flatpickr-day.selected:hover,
.flatpickr-day.startRange:hover,
.flatpickr-day.endRange:hover {
  background: #0066cc;
  border-color: #0066cc;
  color: #fff;
}

.flatpickr-day.flatpickr-disabled {
  color: rgba(57, 57, 57, 0.35) !important;
  background: transparent !important;
  border-color: transparent !important;
  cursor: not-allowed !important;
  pointer-events: none !important;
}

.flatpickr-day.flatpickr-disabled:hover {
  background: transparent !important;
  border-color: transparent !important;
}

.flatpickr-day.prevMonthDay,
.flatpickr-day.nextMonthDay {
  color: rgba(57, 57, 57, 0.35) !important;
}

@media (max-width: 768px) {
  .flatpickr-calendar.open {
    font-size: 12px;
    line-height: 1.2;
    width: min(100%, calc(100vw - 24px)) !important;
    max-width: calc(100vw - 24px);
  }

  .flatpickr-current-month {
    font-size: 0.85rem;
  }

  .flatpickr-current-month .flatpickr-monthDropdown-months,
  .flatpickr-current-month input.cur-year {
    font-size: 0.85rem;
  }

  .flatpickr-weekdays .flatpickr-weekdaycontainer {
    width: 100%;
  }

  .flatpickr-days,
  .dayContainer {
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
  }

  .dayContainer {
    justify-content: flex-start !important;
  }

  span.flatpickr-weekday,
  .flatpickr-day {
    flex: 0 0 14.2857143% !important;
    width: 14.2857143% !important;
    max-width: none !important;
    box-sizing: border-box;
  }

  .flatpickr-weekday {
    font-size: 0.65rem;
  }

  .flatpickr-day {
    height: 36px;
    line-height: 36px;
    font-size: 0.8rem;
  }

  .flatpickr-day.selected,
  .flatpickr-day.startRange,
  .flatpickr-day.endRange {
    line-height: 36px;
  }
}
</style>
